import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, ArrowRight, Save, Check, Sparkles } from "lucide-react";
import {
  STEPS, SERVICE_AREAS, COMPETITIVE_ADVANTAGES, IDEAL_CUSTOMERS, INCOMES,
  BRAND_PERSONALITY, SONG_PURPOSES, GENRES, MOODS, ENERGY_LEVELS, TEMPOS,
  VOCAL_GENDERS, VOCAL_STYLES, INSTRUMENTS, LYRICS_CONTENT, CALL_TO_ACTION,
  SONG_LENGTHS, VIDEO_ADDONS, YEARS_DROPDOWN,
} from "./brief/briefConstants";
import { Field, TextArea, SelectField, ChipMulti, RadioPills } from "./brief/briefFields";

const EMPTY = {
  business_profile: {
    business_name: "", legal_name: "", slogan: "", mission: "", vision: "",
    year_established: "", years_in_business: "",
    website: "", phone: "", email: "",
    address: { street: "", city: "", state: "", zip: "", country: "USA" },
    service_area: [],
  },
  business_description: {
    description: "", products: [], services: [],
    competitive_advantages: [], advantages_other: "",
  },
  target_customer: {
    ideal_customer: [], gender: "", household_income: "",
    interests: "", problems_solved: "",
  },
  brand_personality: [],
  song_purpose: [], song_purpose_other: "",
  primary_genre: "", secondary_genre: "",
  mood: [], energy: "", tempo: "",
  vocal: { gender: "", styles: [] },
  instruments: [],
  lyrics_content: [],
  call_to_action: "",
  song_length: "",
  artist_references: "",
  video_addon: "No",
  special_instructions: "",
};

export default function BriefWizard() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [brief, setBrief] = useState(EMPTY);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      setBrief({ ...EMPTY, ...(data.brief || {}) });
      setStep(Math.min(data.brief_progress || 0, STEPS.length - 1));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Auto-calc years_in_business
  useEffect(() => {
    const y = parseInt(brief.business_profile?.year_established || 0, 10);
    if (y > 1900) {
      const yib = new Date().getFullYear() - y;
      if (yib !== brief.business_profile.years_in_business) {
        setBrief((b) => ({
          ...b,
          business_profile: { ...b.business_profile, years_in_business: yib },
        }));
      }
    }
  }, [brief.business_profile?.year_established]); // eslint-disable-line

  const save = async (silent = false, nextStep = step) => {
    setSaving(true); setError("");
    try {
      await api.put(`/projects/${id}/brief`, { data: brief, progress: nextStep });
      setSavedAt(new Date());
      if (!silent) setSavedAt(new Date());
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    await save(true, Math.min(step + 1, STEPS.length - 1));
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // === Generic patch helpers ===
  const patch = (path, value) => {
    setBrief((b) => {
      const next = JSON.parse(JSON.stringify(b));
      const segs = path.split(".");
      let ref = next;
      for (let i = 0; i < segs.length - 1; i++) ref = ref[segs[i]];
      ref[segs[segs.length - 1]] = value;
      return next;
    });
  };

  const finish = async () => {
    await save(true, STEPS.length - 1);
    nav(`/dashboard/projects/${id}/lyrics`);
  };

  if (!project) return <div className="container-x py-20 text-white/60">Loading…</div>;
  if (project.status !== "brief_draft") {
    return (
      <div className="container-x py-20 text-center">
        <p className="text-white/70 mb-4">This brief has already been submitted.</p>
        <button onClick={() => nav(`/dashboard/projects/${id}`)} className="btn-gold">View Project</button>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-[calc(100vh-80px)]">
      <div className="container-x py-10 max-w-4xl">
        <div className="mb-3 eyebrow">Business Discovery</div>
        <h1 className="font-heading text-3xl md:text-4xl mb-3">Custom Song Creation Brief</h1>
        <p className="text-white/55 mb-8 text-sm">Step {step + 1} of {STEPS.length} — {STEPS[step].label}</p>

        {/* Progress */}
        <div className="flex items-center gap-1 mb-10">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex-1">
              <div className={`h-1.5 ${i <= step ? "bg-gold" : "bg-ink-700"}`} />
              <div className={`text-[10px] mt-2 tracking-widest uppercase ${i === step ? "text-gold" : "text-white/40"}`}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Steps */}
        {step === 0 && <Step1 brief={brief} patch={patch} />}
        {step === 1 && <Step2 brief={brief} patch={patch} />}
        {step === 2 && <Step3 brief={brief} patch={patch} />}
        {step === 3 && <Step4 brief={brief} patch={patch} />}
        {step === 4 && <Step5 brief={brief} patch={patch} />}
        {step === 5 && <Step6 brief={brief} patch={patch} />}

        {error && <div className="text-destructive mt-4 text-sm" data-testid="brief-error">{error}</div>}

        <div className="flex items-center justify-between gap-4 mt-10 flex-wrap">
          <div className="flex gap-3">
            {step > 0 && <button onClick={goBack} className="btn-outline" data-testid="brief-back"><ArrowLeft size={14} /> Back</button>}
            <button onClick={() => save(false)} disabled={saving} className="btn-outline" data-testid="brief-save-draft">
              <Save size={14} /> {saving ? "Saving…" : "Save Draft"}
            </button>
            {savedAt && <span className="text-white/40 text-xs self-center">Saved {savedAt.toLocaleTimeString()}</span>}
          </div>
          {step < STEPS.length - 1 ? (
            <button onClick={goNext} className="btn-gold" data-testid="brief-next">Continue <ArrowRight size={14} /></button>
          ) : (
            <button onClick={finish} className="btn-gold" data-testid="brief-finish">
              <Sparkles size={14} /> Continue to Lyrics
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============ STEPS ============ */

function Step1({ brief, patch }) {
  const bp = brief.business_profile || {};
  const addr = bp.address || {};
  return (
    <div className="space-y-8" data-testid="brief-step-business">
      <div>
        <div className="eyebrow mb-3">Section 1 — Business Profile</div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Business Name" value={bp.business_name} onChange={(v) => patch("business_profile.business_name", v)} testid="bp-name" required />
          <Field label="Legal Business Name (if different)" value={bp.legal_name} onChange={(v) => patch("business_profile.legal_name", v)} testid="bp-legal" />
          <Field label="Slogan / Tagline" value={bp.slogan} onChange={(v) => patch("business_profile.slogan", v)} placeholder='e.g. "Service You Can Trust"' testid="bp-slogan" full />
          <TextArea label="Mission Statement" value={bp.mission} onChange={(v) => patch("business_profile.mission", v)} testid="bp-mission" rows={3} />
          <TextArea label="Vision Statement" value={bp.vision} onChange={(v) => patch("business_profile.vision", v)} testid="bp-vision" rows={3} />
          <SelectField label="Year Established" value={String(bp.year_established || "")} onChange={(v) => patch("business_profile.year_established", v)} options={YEARS_DROPDOWN.map(String)} testid="bp-year" />
          <Field label="Years in Business (auto)" value={bp.years_in_business || ""} onChange={() => {}} testid="bp-yib" />
          <Field label="Business Website" value={bp.website} onChange={(v) => patch("business_profile.website", v)} placeholder="https://" testid="bp-website" full />
          <Field label="Business Phone" value={bp.phone} onChange={(v) => patch("business_profile.phone", v)} testid="bp-phone" />
          <Field label="Business Email" value={bp.email} onChange={(v) => patch("business_profile.email", v)} type="email" testid="bp-email" />
        </div>
      </div>

      <div>
        <div className="eyebrow mb-3">Physical Address</div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Street Address" value={addr.street} onChange={(v) => patch("business_profile.address.street", v)} testid="addr-street" full />
          <Field label="City" value={addr.city} onChange={(v) => patch("business_profile.address.city", v)} testid="addr-city" />
          <Field label="State" value={addr.state} onChange={(v) => patch("business_profile.address.state", v)} testid="addr-state" />
          <Field label="Zip Code" value={addr.zip} onChange={(v) => patch("business_profile.address.zip", v)} testid="addr-zip" />
          <Field label="Country" value={addr.country} onChange={(v) => patch("business_profile.address.country", v)} testid="addr-country" />
        </div>
      </div>

      <ChipMulti label="Service Area" options={SERVICE_AREAS} values={bp.service_area || []} onChange={(v) => patch("business_profile.service_area", v)} testid="service-area" />
    </div>
  );
}

function Step2({ brief, patch }) {
  const bd = brief.business_description || {};
  const updateProduct = (i, key, val) => {
    const next = [...(bd.products || [])];
    next[i] = { ...next[i], [key]: val };
    patch("business_description.products", next);
  };
  const updateService = (i, key, val) => {
    const next = [...(bd.services || [])];
    next[i] = { ...next[i], [key]: val };
    patch("business_description.services", next);
  };
  return (
    <div className="space-y-8" data-testid="brief-step-description">
      <TextArea label="Describe Your Business" value={bd.description} onChange={(v) => patch("business_description.description", v)} testid="bd-description" rows={5}
                placeholder="What your company does · Why customers choose you · What makes you unique · What problem you solve" />

      <RepeatList
        title="Products Offered"
        items={bd.products || []}
        onAdd={() => patch("business_description.products", [...(bd.products || []), { name: "", description: "", price_range: "", key_benefits: "" }])}
        onRemove={(i) => patch("business_description.products", (bd.products || []).filter((_, idx) => idx !== i))}
        renderItem={(it, i) => (
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Product Name" value={it.name} onChange={(v) => updateProduct(i, "name", v)} testid={`product-${i}-name`} />
            <Field label="Price Range" value={it.price_range} onChange={(v) => updateProduct(i, "price_range", v)} testid={`product-${i}-price`} />
            <Field label="Description" value={it.description} onChange={(v) => updateProduct(i, "description", v)} testid={`product-${i}-desc`} full />
            <Field label="Key Benefits" value={it.key_benefits} onChange={(v) => updateProduct(i, "key_benefits", v)} testid={`product-${i}-benefits`} full />
          </div>
        )}
        testid="products"
      />

      <RepeatList
        title="Services Offered"
        items={bd.services || []}
        onAdd={() => patch("business_description.services", [...(bd.services || []), { name: "", description: "", price_range: "", key_benefits: "" }])}
        onRemove={(i) => patch("business_description.services", (bd.services || []).filter((_, idx) => idx !== i))}
        renderItem={(it, i) => (
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Service Name" value={it.name} onChange={(v) => updateService(i, "name", v)} testid={`service-${i}-name`} />
            <Field label="Price Range" value={it.price_range} onChange={(v) => updateService(i, "price_range", v)} testid={`service-${i}-price`} />
            <Field label="Description" value={it.description} onChange={(v) => updateService(i, "description", v)} testid={`service-${i}-desc`} full />
            <Field label="Key Benefits" value={it.key_benefits} onChange={(v) => updateService(i, "key_benefits", v)} testid={`service-${i}-benefits`} full />
          </div>
        )}
        testid="services"
      />

      <ChipMulti label="Competitive Advantages (select all)" options={COMPETITIVE_ADVANTAGES}
        values={bd.competitive_advantages || []}
        onChange={(v) => patch("business_description.competitive_advantages", v)} testid="advantage" />
      <Field label="Other Advantage" value={bd.advantages_other} onChange={(v) => patch("business_description.advantages_other", v)} testid="advantage-other" full />
    </div>
  );
}

function RepeatList({ title, items, onAdd, onRemove, renderItem, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow">{title}</div>
        <button type="button" onClick={onAdd} className="btn-outline !py-2 !px-4 !text-xs" data-testid={`${testid}-add`}>+ Add</button>
      </div>
      <div className="space-y-4">
        {(items || []).map((it, i) => (
          <div key={i} className="border border-ink-700 p-4 bg-ink-900/50 relative" data-testid={`${testid}-item-${i}`}>
            {renderItem(it, i)}
            <button type="button" onClick={() => onRemove(i)} className="absolute top-2 right-2 text-destructive text-xs hover:underline" data-testid={`${testid}-remove-${i}`}>Remove</button>
          </div>
        ))}
        {(!items || items.length === 0) && <div className="text-white/40 text-sm italic">None added yet.</div>}
      </div>
    </div>
  );
}

function Step3({ brief, patch }) {
  const tc = brief.target_customer || {};
  return (
    <div className="space-y-8" data-testid="brief-step-audience">
      <div>
        <div className="eyebrow mb-3">Section 3 — Target Customer</div>
        <ChipMulti label="Ideal Customer" options={IDEAL_CUSTOMERS} values={tc.ideal_customer || []}
          onChange={(v) => patch("target_customer.ideal_customer", v)} testid="cust-type" />
        <div className="grid sm:grid-cols-2 gap-5 mt-5">
          <RadioPills label="Gender" options={["Male", "Female", "Both"]} value={tc.gender} onChange={(v) => patch("target_customer.gender", v)} testid="cust-gender" />
          <RadioPills label="Household Income" options={INCOMES} value={tc.household_income} onChange={(v) => patch("target_customer.household_income", v)} testid="cust-income" />
        </div>
        <div className="grid sm:grid-cols-2 gap-5 mt-2">
          <TextArea label="Customer Interests" value={tc.interests} onChange={(v) => patch("target_customer.interests", v)} testid="cust-interests" />
          <TextArea label="Customer Problems You Solve" value={tc.problems_solved} onChange={(v) => patch("target_customer.problems_solved", v)} testid="cust-problems" />
        </div>
      </div>

      <div>
        <div className="eyebrow mb-3">Section 4 — Brand Personality (up to 10 words)</div>
        <ChipMulti label="" options={BRAND_PERSONALITY} values={brief.brand_personality || []} max={10}
          onChange={(v) => patch("brand_personality", v)} testid="personality" />
      </div>
    </div>
  );
}

function Step4({ brief, patch }) {
  return (
    <div className="space-y-8" data-testid="brief-step-song">
      <div>
        <div className="eyebrow mb-3">Section 6 — Song Purpose</div>
        <ChipMulti label="What is the song for? (select all)" options={SONG_PURPOSES} values={brief.song_purpose || []}
          onChange={(v) => patch("song_purpose", v)} testid="purpose" />
        <div className="mt-3"><Field label="Other Purpose" value={brief.song_purpose_other} onChange={(v) => patch("song_purpose_other", v)} testid="purpose-other" full /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <SelectField label="Primary Genre" value={brief.primary_genre} onChange={(v) => patch("primary_genre", v)} options={GENRES} testid="genre-primary" />
        <SelectField label="Secondary Genre (optional)" value={brief.secondary_genre} onChange={(v) => patch("secondary_genre", v)} options={GENRES} testid="genre-secondary" />
      </div>
      <ChipMulti label="Mood (up to 5)" options={MOODS} values={brief.mood || []} max={5}
        onChange={(v) => patch("mood", v)} testid="mood" />
      <RadioPills label="Energy Level" options={ENERGY_LEVELS} value={brief.energy} onChange={(v) => patch("energy", v)} testid="energy" />
      <RadioPills label="Tempo" options={TEMPOS} value={brief.tempo} onChange={(v) => patch("tempo", v)} testid="tempo" />
    </div>
  );
}

function Step5({ brief, patch }) {
  const vocal = brief.vocal || {};
  return (
    <div className="space-y-8" data-testid="brief-step-vocals">
      <div>
        <div className="eyebrow mb-3">Section 11 — Vocal Selection</div>
        <RadioPills label="Vocal Gender" options={VOCAL_GENDERS} value={vocal.gender} onChange={(v) => patch("vocal.gender", v)} testid="vocal-gender" />
        <div className="mt-4">
          <ChipMulti label="Vocal Style (select all)" options={VOCAL_STYLES} values={vocal.styles || []}
            onChange={(v) => patch("vocal.styles", v)} testid="vocal-style" />
        </div>
      </div>
      <ChipMulti label="Section 12 — Musical Instruments (select all)" options={INSTRUMENTS} values={brief.instruments || []}
        onChange={(v) => patch("instruments", v)} testid="instruments" />
      <ChipMulti label="Section 13 — Lyrics Content (select all that apply)" options={LYRICS_CONTENT} values={brief.lyrics_content || []}
        onChange={(v) => patch("lyrics_content", v)} testid="lyrics-content" />
    </div>
  );
}

function Step6({ brief, patch }) {
  return (
    <div className="space-y-8" data-testid="brief-step-final">
      <RadioPills label="Section 14 — Call to Action" options={CALL_TO_ACTION} value={brief.call_to_action} onChange={(v) => patch("call_to_action", v)} testid="cta" />
      <RadioPills label="Section 15 — Song Length" options={SONG_LENGTHS} value={brief.song_length} onChange={(v) => patch("song_length", v)} testid="length" />
      <TextArea label="Section 16 — Artist References (e.g. Luke Combs, Bruno Mars, CeCe Winans, Imagine Dragons)"
        value={brief.artist_references} onChange={(v) => patch("artist_references", v)} testid="artist-refs" rows={3} />
      <RadioPills label="Section 17 — Video Production Add-On (optional, separately priced)" options={VIDEO_ADDONS} value={brief.video_addon} onChange={(v) => patch("video_addon", v)} testid="video-addon" />
      <TextArea label="Section 19 — Special Instructions"
        placeholder="Anything else you want included in lyrics, music, vocals, branding, storytelling…"
        value={brief.special_instructions} onChange={(v) => patch("special_instructions", v)} testid="special" rows={4} />

      <div className="bg-gold/5 border border-gold/30 p-5 flex items-start gap-3">
        <Check className="text-gold mt-1" size={18} />
        <div>
          <div className="text-gold font-semibold mb-1">You&apos;re ready to generate your lyrics</div>
          <p className="text-white/70 text-sm">
            Click <strong>Continue to Lyrics</strong> below. Our AI will use everything you&apos;ve shared to draft original lyrics tailored to your brand.
            You&apos;ll be able to review, edit, and approve the lyrics before they go to music production.
          </p>
        </div>
      </div>
    </div>
  );
}
